import React, {useState, useRef, ReactNode, useEffect, useCallback, useMemo} from 'react'
import {v4 as uuidv4} from 'uuid'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {useAppState} from '../../../state'
import {useTranslation} from 'react-i18next'
import {Grid, Baseline, InvisibleContainer, GridSpan} from '../../Layout/Grid'
import {Select, Toggler, BigLabelInput, Label, LabelInputWrapper} from '../../Atoms/Forms'
import styled from 'styled-components'
import {IconButton, Button, ButtonRowWrap, ButtonRowGrid} from '../../Atoms/Buttons'
import {IconCloseSmall} from '../../../Assets/Icons/TinyIcons'
import {useRestaurantListField, useTaskForm, useTaskGroupSelect} from '../../../config/utils'
import {colors} from '../../../sharedComponents/colors'
import {TodoSubTask, TodoTaskResponse} from '../../../state/rest'
import Scheduler from '../../Organisms/Scheduler/Scheduler'
import {MainLayoutWithoutStretch, RightSidebar, MainContent} from '../../Layout/Layout'
import LocationsList from '../../Organisms/Locations/LocationsList'
import {HeaderRow, HeaderActions} from '../../Molecules/ViewComponents/ViewHeader'
import TaskMenuModal from './TaskMenuModal'
import {P} from '../../Atoms/Typography'
import {CommonInformationFields, TasksGoBackHeaderTitle} from './CommonTasksComponents'
import IsUserHealthInspector from '../../../hooks/userIsHealthInspector'
import {DefaultTagsWidget} from '../../Organisms/Tags/DefaultSuggestionWidget'

interface FormProps {
  savedData?: TodoTaskResponse
}
const TASK_KEY = 'todo'

const CreateTodoTask = ({savedData}: FormProps) => {
  const {t} = useTranslation('tasks')
  const {state} = useAppState()
  const {isHealthInspector} = IsUserHealthInspector()

  const isEditing = !!savedData && !!savedData.id
  const isDuplicate = !!savedData && !savedData.id

  const saved = savedData
    ? isDuplicate && savedData.name
      ? {
          ...savedData,
          name: `${t('tasks:labels.copyOf', 'Copy of')} ${savedData.name.toLowerCase()}`,
          sites: [state.site!]
        }
      : savedData
    : undefined

  // task form controller hook
  const fc = useTaskForm(TASK_KEY, saved)
  const rc = useRestaurantListField(
    {watch: fc.watch, setValue: fc.setValue},
    isDuplicate ? undefined : saved ? saved.sites : undefined
  )

  const {taskGroupOptions, selectTaskGroup, selectedTaskGroup} = useTaskGroupSelect(
    TASK_KEY,
    saved ? saved.taskGroup : undefined
  )

  const [subTasks, setSubtasks] = useState<TodoSubTask[]>(saved ? saved.subTasks || [] : [])

  const [showUntilCompleted, setShowUntilCompleted] = useState(saved ? saved.showUntilCompleted : false)
  const handleTogglerChange = (value: boolean) => {
    setShowUntilCompleted(value)
  }

  const normalizedSubTasks = subTasks
    .map(subTask => ({
      ...subTask,
      name: subTask.name.trim(),
      options: subTask.options
        .map(option => option.trim())
        .filter((option, index, options) => option.length > 0 && options.indexOf(option) === index)
    }))
    .filter(subTask => subTask.name.length > 0)

  const taskPayload = {
    taskGroup: selectedTaskGroup,
    subTasks: normalizedSubTasks,
    assets: fc.assets,
    schedule: fc.scheduledPayload,
    sites: rc.list,
    showUntilCompleted: showUntilCompleted,
    lateByDays: fc.lateByDays,
    relatedSites: fc.relatedSites
  }

  return (
    <MainLayoutWithoutStretch>
      <MainContent variant="white">
        <HeaderRow
          modal={
            isEditing && (
              <TaskMenuModal
                isOpen={fc.modalOpen}
                onClose={() => fc.setModalOpen(false)}
                onDelete={fc.editingDisabled ? undefined : fc.remove}
                onDuplicate={fc.duplicate}
              />
            )
          }
        >
          <TasksGoBackHeaderTitle
            label={
              isEditing
                ? t('tasks:labels.editTask.todo', 'Edit checklist task')
                : t('tasks:labels.newTask.todo', 'Add checklist task')
            }
            isEditing={isEditing}
          />
          {isEditing && <HeaderActions onMenu={() => fc.setModalOpen(true)} />}
        </HeaderRow>
        <InvisibleContainer>
          <Baseline>
            <Grid>
              <BigLabelInput
                required
                placeholder={t('tasks:placeholders.newTaskName', 'New task name')}
                name="name"
                id="name"
                labelText={t('tasks:labels.taskName', 'Task name')}
                ref={fc.register({required: `${t('common:validation.requiredField', 'Required field')}`})}
                errorMsg={!!fc.errors.name ? fc.errors.name!.message : undefined}
              ></BigLabelInput>
              <div className="empty"></div>
            </Grid>
            <Grid>
              <Baseline>
                <Select
                  returnId
                  nativeProps={{
                    name: 'taskGroup',
                    onChange(e) {
                      selectTaskGroup(e.target.value)
                    },
                    value: selectedTaskGroup.id
                  }}
                  id="taskGroup"
                  label={t('tasks:labels.taskGroup', 'Task group')}
                  options={taskGroupOptions}
                />

                <div>
                  <LabelInputWrapper>
                    <Label>{t('tasks:labels.addSubtasks', 'Add subtasks')}</Label>
                  </LabelInputWrapper>
                  <TodoWidget
                    onChange={items => {
                      setSubtasks(items)
                    }}
                    savedTodos={subTasks}
                    buttonText={t('tasks:labels.addSubtask', 'Add a subtask')}
                    placeholderText={t('tasks:placeholders.checklistItem', 'Checklist item')}
                  />
                </div>

                <Toggler
                  label={t('tasks:labels.showUntilCompleted', 'Show until completed')}
                  initiallyChecked={showUntilCompleted}
                  name="isShowUntilCompletedTask"
                  values={[t('common:togglerValues.no', 'No'), t('common:togglerValues.yes', 'Yes')]}
                  onClick={() => handleTogglerChange(!showUntilCompleted)}
                />

                <Toggler
                  label={t('tasks:labels.scheduledTask', 'Scheduled task')}
                  values={[t('common:togglerValues.no', 'No'), t('common:togglerValues.yes', 'Yes')]}
                  initiallyChecked={!!saved && !!saved.schedule}
                  name="isScheduledTask"
                  onClick={() => {
                    fc.toggleShow()
                  }}
                />

                {!!fc.scheduledPayload ? (
                  <Scheduler
                    onChange={fc.handleScheduler}
                    schedule={saved ? fc.reverseSchedule(saved.schedule) : undefined}
                    lateByDays={saved ? fc.lateByDays : undefined}
                    onLateByDaysChange={fc.setLateByDays}
                  />
                ) : null}
              </Baseline>

              <Baseline>
                <CommonInformationFields useForm={fc} />
                <DefaultTagsWidget
                  onChange={items => fc.setRelatedSites(items)}
                  savedItems={saved ? saved.relatedSites : undefined}
                  title={t('tasks:labels.relatedSites', 'Related sites')}
                />
              </Baseline>
            </Grid>

            <ButtonRowWrap>
              <div className="empty"></div>
              <ButtonRowGrid>
                <Button name="cancel-button" negative variant="secondary" disabled={fc.loading} onClick={fc.cancel}>
                  {t('common:buttons.cancel', 'Cancel')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={
                    !fc.formState.isValid ||
                    fc.loading ||
                    rc.list.length === 0 ||
                    fc.editingDisabled ||
                    isHealthInspector
                  }
                  onClick={() => (isEditing ? fc.update(taskPayload) : fc.submit(taskPayload))}
                >
                  {t('common:buttons.save', 'Save')}
                </Button>

                {fc.editingDisabled && (
                  <GridSpan span={2}>
                    <P>
                      {t(
                        'tasks:messages.taskInUseMessage',
                        'This task is in use for locations you don’t have access to. You can not edit or delete it but you can duplicate the task to create a new one'
                      )}
                    </P>
                  </GridSpan>
                )}
              </ButtonRowGrid>
            </ButtonRowWrap>
          </Baseline>
        </InvisibleContainer>
      </MainContent>
      <RightSidebar>
        <LocationsList
          controller={rc}
          cyKey={'task'}
          label={t('tasks:labels.selectLocation', 'Select which locations use this task')}
        />
      </RightSidebar>
    </MainLayoutWithoutStretch>
  )
}

const DragHandle = styled.div`
  width: 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4px;
  cursor: grab;
  span {
    display: block;
    width: 12px;
    height: 2px;
    background: ${colors.system.grey_2};
    margin: 2px 0;
    border-radius: 2px;
  }
`

const TodoItem = styled.li`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  input.todoItem {
    margin-left: 1rem;
    margin-right: 1rem;
    outline: none;
    border: none;
    min-width: 200px;
    width: auto;
    &::placeholder {
      color: gray;
    }
  }
  .removeIcon {
  }
`

const OptionsList = styled.div`
  flex: 1 1 100%;
  margin: 0.25rem 0 0.5rem 2rem;
  padding: 0.5rem 0.75rem;
  border-left: 2px solid ${colors.system.grey_4};
  color: ${colors.system.grey_1};
  font-size: 0.8rem;
  strong {
    display: block;
    margin-bottom: 0.35rem;
  }
`

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  max-width: 28rem;
  margin-bottom: 0.25rem;
  input {
    flex: 1;
    min-width: 0;
    padding: 0.35rem 0.5rem;
    border: 1px solid ${colors.system.grey_4};
    border-radius: 3px;
  }
`

const OptionDragHandle = styled(DragHandle)`
  width: 14px;
  padding: 2px;
  margin-right: 0.25rem;
  span {
    width: 10px;
  }
`

const AddOptionButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: ${colors.system.grey_1};
  cursor: pointer;
  span {
    margin-right: 0.35rem;
    font-size: 1rem;
  }
`

const TodoList = styled.ul`
  margin: 0;
  padding: 0;
`

interface TodoWidgetProps {
  onChange: (items: TodoSubTask[]) => void
  savedTodos?: TodoSubTask[]
  buttonText: string
  placeholderText: string
  icon?: ReactNode
  showOptions?: boolean
}

const FakeInputButton = styled.button`
  background-color: white;
  border: none;
  color: ${colors.system.grey_1};
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  padding: 0;
  .icon {
    width: 18px;
    height: 18px;
    display: inline-block;
    position: relative;
  }
  .icon::before,
  .icon::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: ${colors.system.grey_2};
    border-radius: 2px;
  }
  .icon::before {
    width: 10px;
    height: 2px;
  }
  .icon::after {
    width: 2px;
    height: 10px;
  }
  .content {
    margin-left: 1rem;
    margin-right: 1rem;
  }
`

interface SortableTodoItemProps {
  id: string
  label: string
  placeholder: string
  autoFocus: boolean
  options: TodoOption[]
  onChange: (val: string) => void
  onOptionChange: (id: string, value: string) => void
  onOptionReorder: (activeId: string, overId: string) => void
  onAddOption: () => void
  onRemoveOption: (id: string) => void
  showOptions: boolean
  onRemove: () => void
  onEnter?: () => void
  inputRef?: (el: HTMLInputElement | null) => void
}

interface SortableOptionRowProps {
  option: TodoOption
  index: number
  onChange: (value: string) => void
  onRemove: () => void
}

const SortableOptionRow = ({option, index, onChange, onRemove}: SortableOptionRowProps) => {
  const {t} = useTranslation('tasks')
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: option.id})
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1
  }

  return (
    <OptionRow ref={setNodeRef as any} style={style}>
      <OptionDragHandle
        {...listeners}
        {...attributes}
        aria-label={t('tasks:labels.reorderOption', 'Reorder option {{index}}', {index: index + 1})}
      >
        <span />
        <span />
        <span />
      </OptionDragHandle>
      <input
        aria-label={t('tasks:labels.optionNumber', 'Option {{index}}', {index: index + 1})}
        placeholder={t('tasks:labels.option', 'Option')}
        value={option.value}
        onChange={e => onChange(e.target.value)}
      />
      <IconButton icon={<IconCloseSmall />} buttonProps={{onClick: onRemove}} />
    </OptionRow>
  )
}

const SortableTodoItem = ({
  id,
  label,
  placeholder,
  autoFocus,
  options,
  onChange,
  onOptionChange,
  onOptionReorder,
  onAddOption,
  onRemoveOption,
  showOptions,
  onRemove,
  onEnter,
  inputRef
}: SortableTodoItemProps) => {
  const {t} = useTranslation('tasks')
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id})
  const optionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  )
  const optionIds = useMemo(() => options.map(option => option.id), [options])
  const handleOptionDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.over && event.active.id !== event.over.id) {
        onOptionReorder(String(event.active.id), String(event.over.id))
      }
    },
    [onOptionReorder]
  )
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1
  }

  return (
    <TodoItem ref={setNodeRef as any} style={style}>
      <DragHandle {...listeners} {...attributes} aria-hidden="true">
        <span />
        <span />
        <span />
      </DragHandle>
      <form
        onSubmit={e => {
          e.preventDefault()
          if (typeof onEnter === 'function') onEnter()
        }}
      >
        <input
          placeholder={placeholder}
          aria-label={t('tasks:labels.editItem', 'Edit item: {{label}}', {label})}
          className="todoItem"
          value={label}
          onChange={e => onChange(e.target.value)}
          autoFocus={autoFocus}
          ref={inputRef as any}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (typeof onEnter === 'function') onEnter()
            }
          }}
        ></input>
      </form>
      <IconButton icon={<IconCloseSmall />} buttonProps={{className: 'removeIcon', onClick: onRemove}} />
      {showOptions && (
        <OptionsList>
          <strong>{t('tasks:labels.singleChoiceOptions', 'Single-choice options')}</strong>
          <DndContext sensors={optionSensors} collisionDetection={closestCenter} onDragEnd={handleOptionDragEnd}>
            <SortableContext items={optionIds} strategy={verticalListSortingStrategy}>
              {options.map((option, index) => (
                <SortableOptionRow
                  key={option.id}
                  option={option}
                  index={index}
                  onChange={value => onOptionChange(option.id, value)}
                  onRemove={() => onRemoveOption(option.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <AddOptionButton type="button" onClick={onAddOption}>
            <span aria-hidden="true">+</span> {t('tasks:labels.addOption', 'Add option')}
          </AddOptionButton>
        </OptionsList>
      )}
    </TodoItem>
  )
}

interface TodoOption {
  id: string
  value: string
}

export const TodoWidget = ({
  onChange,
  savedTodos,
  placeholderText,
  buttonText,
  icon,
  showOptions = true
}: TodoWidgetProps) => {
  const [todos, setTodos] = useState<{id: string; label: string; options: TodoOption[]}[]>(
    (savedTodos || []).map(s => ({
      id: uuidv4(),
      label: s.name,
      options: (s.options || []).map(value => ({id: uuidv4(), value}))
    }))
  )
  const [autoFocusIndex, setAutoFocusIndex] = useState<number | null>(null)
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  useEffect(() => {
    if (autoFocusIndex === null) return
    const id = todos[autoFocusIndex]?.id
    if (!id) return
    const el = inputRefs.current[id]
    if (el) {
      el.focus()
      try {
        el.selectionStart = el.value.length
      } catch (e) {
        // ignore
      }
    }
    setAutoFocusIndex(null)
  }, [autoFocusIndex, todos])
  const handleRemove = (index: number) => {
    const newTodos = todos.filter((_, i) => i !== index)
    setTodos(newTodos)
    setAutoFocusIndex(null)
    onChange(newTodos.map(t => ({name: t.label, options: t.options.map(option => option.value)})))
  }
  const handleAdd = () => {
    const newTodos: {id: string; label: string; options: TodoOption[]}[] = [
      ...todos,
      {id: uuidv4(), label: '', options: []}
    ]
    setAutoFocusIndex(newTodos.length - 1)
    setTodos(newTodos)
    onChange(newTodos.map(t => ({name: t.label, options: t.options.map(option => option.value)})))
  }

  const handleEdit = (index: number, label: string) => {
    const newTodos = [...todos]
    newTodos[index] = {...newTodos[index], label}
    setTodos(newTodos)
    setAutoFocusIndex(null)

    onChange(newTodos.map(t => ({name: t.label, options: t.options.map(option => option.value)})))
  }

  const updateOptions = (todoIndex: number, options: TodoOption[]) => {
    const newTodos = [...todos]
    newTodos[todoIndex] = {...newTodos[todoIndex], options}
    setTodos(newTodos)
    onChange(newTodos.map(t => ({name: t.label, options: t.options.map(option => option.value)})))
  }

  const sensors = useSensors(useSensor(PointerSensor))

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event
    if (!over || active.id === over.id) return
    const oldIndex = todos.findIndex(t => t.id === active.id)
    const newIndex = todos.findIndex(t => t.id === over.id)
    const newItems = arrayMove(todos, oldIndex, newIndex) as {id: string; label: string; options: TodoOption[]}[]
    setTodos(newItems)
    onChange(newItems.map(t => ({name: t.label, options: t.options.map(option => option.value)})))
  }

  return (
    <Baseline>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={todos.map(t => t.id)} strategy={verticalListSortingStrategy}>
          <TodoList>
            {todos.map((todo, index) => (
              <SortableTodoItem
                key={todo.id}
                id={todo.id}
                label={todo.label}
                options={todo.options}
                showOptions={showOptions}
                placeholder={placeholderText}
                autoFocus={index === autoFocusIndex}
                onChange={(val: string) => handleEdit(index, val)}
                onOptionChange={(optionId, value) => {
                  const options = todo.options.map(option => (option.id === optionId ? {...option, value} : option))
                  updateOptions(index, options)
                }}
                onOptionReorder={(activeId, overId) => {
                  const oldIndex = todo.options.findIndex(option => option.id === activeId)
                  const newIndex = todo.options.findIndex(option => option.id === overId)
                  if (oldIndex >= 0 && newIndex >= 0) updateOptions(index, arrayMove(todo.options, oldIndex, newIndex))
                }}
                onAddOption={() => updateOptions(index, [...todo.options, {id: uuidv4(), value: ''}])}
                onRemoveOption={optionId =>
                  updateOptions(
                    index,
                    todo.options.filter(option => option.id !== optionId)
                  )
                }
                onRemove={() => handleRemove(index)}
                inputRef={el => (inputRefs.current[todo.id] = el)}
                onEnter={() => {
                  // insert a new todo after this index and focus it
                  const newTodos = [...todos]
                  const insertIndex = index + 1
                  newTodos.splice(insertIndex, 0, {id: uuidv4(), label: '', options: []})
                  setTodos(newTodos)
                  setAutoFocusIndex(insertIndex)
                  onChange(newTodos.map(t => ({name: t.label, options: t.options.map(option => option.value)})))
                }}
              />
            ))}
          </TodoList>
        </SortableContext>
      </DndContext>

      <FakeInputButton onClick={() => handleAdd()}>
        {icon || <div className="icon" aria-hidden="true"></div>}
        <div className="content">{buttonText}</div>
      </FakeInputButton>
    </Baseline>
  )
}

export default CreateTodoTask
